import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Divider,
  useMediaQuery,
  Skeleton,
  Alert,
} from '@mui/material';
import React from 'react';

import type { StatusActivityAction } from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from '../hooks';
import { TimelineItem } from './TimelineItem.tsx';
import { FilterControls } from './FilterControls.tsx';
import { groupTimelineItemsByDate } from './utils.ts';
import { useIntakeStatusActivity } from './useIntakeStatusActivity.ts';
import { useHistoryDialogFilters } from './useHistoryDialogFilters.ts';
import type { GroupedTimelineItems, TimelineItem as TimelineItemType } from './HistoryDialog.types.ts';

type NumberOfItemsForDateProps = {
  group: GroupedTimelineItems;
};

const NumberOfItemsForDate: React.FC<NumberOfItemsForDateProps> = ({ group }) => {
  const numberOfItems = group.items.length;
  const isMoreThanOne = numberOfItems > 1;

  return (
    <Typography variant="caption" textAlign="left">
      There {isMoreThanOne ? 'were' : 'was'}{' '}
      <b>
        {numberOfItems} activity update{isMoreThanOne ? 's' : ''}
      </b>{' '}
      on this day
    </Typography>
  );
};

const LoadingLayout = () => (
  <Stack gap={2}>
    {Array.from({ length: 7 }).map((_, index) => (
      <Skeleton variant="rounded" height={200} width={'100%'} key={index} animation="wave">
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography variant="body2">Loading...</Typography>
        </Box>
      </Skeleton>
    ))}
  </Stack>
);

type HistoryTimelineProps = {
  isMobile: boolean;
  onStepClick: (item: TimelineItemType<StatusActivityAction>) => void;
};

const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ isMobile, onStepClick }) => {
  const { patientId } = usePatientIntakeStatusContext();
  const { filters } = useHistoryDialogFilters();
  const { timelineItems, isLoading, error } = useIntakeStatusActivity({ patientId, filters });

  if (isLoading) {
    return <LoadingLayout />;
  }

  const groupedItems = groupTimelineItemsByDate(timelineItems);

  if (error) {
    return <Alert severity="error">Failed to load activity history</Alert>;
  }

  if (timelineItems.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2">No activity history available for this patient.</Typography>
      </Box>
    );
  }

  return (
    <Stack gap={1}>
      {groupedItems.map((group, groupIndex) => (
        <React.Fragment key={group.date}>
          <Box position="relative">
            {!isMobile && (
              <Stack
                position="absolute"
                top={4}
                left={0}
                zIndex={1}
                direction="row"
                gap={1}
                width={'100%'}
                alignItems="center"
              >
                <NumberOfItemsForDate group={group} />
                <Box height={2} flexGrow={1} bgcolor="divider" />
              </Stack>
            )}

            <Stack
              position={isMobile ? 'relative' : 'sticky'}
              top={'16px'}
              zIndex={isMobile ? 1 : 11}
              flexDirection="row"
              justifyContent="flex-end"
              pb={2}
            >
              <Stack direction="column" justifyContent="flex-end">
                <Box bgcolor="background.paper" sx={{ alignSelf: 'flex-end', pl: 1 }}>
                  <Typography
                    variant="h6"
                    textAlign="right"
                    alignSelf="flex-end"
                    fontSize={16}
                    sx={{ backgroundColor: 'background.paper' }}
                    width={'fit-content'}
                    pl={1}
                  >
                    {group.date}
                  </Typography>
                </Box>
                <Typography variant="caption" textAlign="right">
                  {group.relativeDate}
                </Typography>
              </Stack>
            </Stack>
            <Stack gap={2}>
              {group.items.map((item) => (
                <TimelineItem key={item.id} item={item} onStepClick={() => onStepClick(item)} isLoading={isLoading} />
              ))}
            </Stack>
          </Box>
          {groupIndex < groupedItems.length - 1 && <Divider sx={{ mt: 1 }} />}
        </React.Fragment>
      ))}
    </Stack>
  );
};

type HistoryDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const HistoryDialog: React.FC<HistoryDialogProps> = ({ open, onClose }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { filters, setFilters } = useHistoryDialogFilters();

  const handleClose = () => {
    onClose();
    setFilters(null);
  };

  const handleOnStepClick = (item: TimelineItemType<StatusActivityAction>) => {
    // If we're already filtering by this stepId, clear the filter
    // Otherwise, filter by the clicked stepId
    if (filters.activeStep?.id === item.stepId) {
      setFilters({ activeStep: null });

      return;
    }

    setFilters({ activeStep: { id: item.stepId, name: item.stepSnapshot.type } });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogContent sx={{ pt: 0 }}>
        <Stack position="sticky" top={0} zIndex={10} bgcolor="background.paper" py={2}>
          <Typography variant="h6" component="div">
            Patient Intake History
          </Typography>
          <Typography variant="body2">Complete timeline of all patient intake steps and activities</Typography>

          <FilterControls />
        </Stack>
        <HistoryTimeline isMobile={isMobile} onStepClick={handleOnStepClick} />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
